/**
 * StagedAttachmentList — the reply composer's staged-file chips (055 US5),
 * extracted from `ReplyComposer` to keep it within the file-size ceiling.
 *
 * Owns the one piece of behaviour that is easy to lose: removing a chip unmounts
 * its focused remove button, which would drop keyboard focus to <body>
 * (WCAG 2.4.3). Focus moves to the next chip's remove button, else the previous
 * one, else `fallbackFocusRef` (the composer's attach button). The component is
 * rendered even when the list is empty so that last hop can still run.
 *
 * Also home to `AttachControl` — the composer's attach button and its
 * `aria-describedby` hints (the visible type/size rule, and the FR-016a count
 * hint only when a count limit is configured) — moved here with the chips to
 * keep `ReplyComposer` within the file-size ceiling.
 */

import { type JSX, type RefObject, useEffect, useId, useRef } from 'react';

import { fill } from '../i18n/fill.js';
import { useTranslate } from '../i18n/useTranslate.js';

/** One file staged for sending, with a stable id for keying and removal. */
export interface StagedFile {
  readonly id: string;
  readonly file: File;
}

/**
 * The attach button plus its descriptions: the type/size rule, visible to
 * everyone (WCAG 3.3.2), and — only when a count limit is configured (055
 * FR-016a) — the localised count hint with the live staged count. Both are the
 * button's `aria-describedby`; the count hint joins only when a limit exists.
 */
export function AttachControl({
  buttonRef,
  className,
  onAttach,
  maxCount,
  stagedCount,
}: {
  readonly buttonRef: RefObject<HTMLButtonElement | null>;
  readonly className: string;
  readonly onAttach: () => void;
  readonly maxCount: number | undefined;
  /** How many files are staged now, so the member sees how close the limit is. */
  readonly stagedCount: number;
}): JSX.Element {
  const t = useTranslate();
  const uploadHintId = useId();
  const countHintId = useId();
  return (
    <div className="flex flex-col items-start gap-1.5">
      <button
        ref={buttonRef}
        type="button"
        aria-describedby={maxCount !== undefined ? `${uploadHintId} ${countHintId}` : uploadHintId}
        className={className}
        onClick={onAttach}
      >
        {t('myCases.detail.chooseFile')}
      </button>
      <span id={uploadHintId} className="pl-0.5 text-xs text-muted-foreground">
        {t('myCases.detail.uploadLabel')}
      </span>
      {maxCount !== undefined ? (
        <span id={countHintId} className="pl-0.5 text-xs text-muted-foreground">
          {fill(t('myCases.detail.attachmentCountHint'), { max: maxCount, count: stagedCount })}
        </span>
      ) : null}
    </div>
  );
}

export function StagedAttachmentList({
  files,
  onRemove,
  fallbackFocusRef,
}: {
  readonly files: readonly StagedFile[];
  readonly onRemove: (id: string) => void;
  readonly fallbackFocusRef: RefObject<HTMLButtonElement | null>;
}): JSX.Element | null {
  const t = useTranslate();
  const listRef = useRef<HTMLUListElement>(null);
  const pendingFocusRef = useRef<string | 'fallback' | null>(null);
  // The chip whose remove button holds focus, and the list as last rendered, so
  // a chip that leaves WITHOUT a remove click (uploaded mid-send) can still hand
  // focus to a neighbour instead of dropping it to <body> (WCAG 2.4.3).
  const focusedIdRef = useRef<string | null>(null);
  const prevFilesRef = useRef<readonly StagedFile[]>(files);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `files` is the trigger — focus moves once the removed chip has left the DOM.
  useEffect(() => {
    const prevFiles = prevFilesRef.current;
    prevFilesRef.current = files;
    const focusedId = focusedIdRef.current;
    if (
      pendingFocusRef.current === null &&
      focusedId !== null &&
      !files.some((f) => f.id === focusedId) &&
      (document.activeElement === null || document.activeElement === document.body)
    ) {
      focusedIdRef.current = null;
      const index = prevFiles.findIndex((f) => f.id === focusedId);
      const survivors = prevFiles.filter((f) => files.some((g) => g.id === f.id));
      const neighbour =
        prevFiles.slice(index + 1).find((f) => survivors.includes(f)) ??
        prevFiles
          .slice(0, Math.max(index, 0))
          .reverse()
          .find((f) => survivors.includes(f));
      pendingFocusRef.current = neighbour !== undefined ? neighbour.id : 'fallback';
    }
    const target = pendingFocusRef.current;
    if (target === null) {
      return;
    }
    pendingFocusRef.current = null;
    if (target === 'fallback') {
      fallbackFocusRef.current?.focus();
      return;
    }
    // The neighbour may itself have left mid-send (uploaded): fall back then too.
    const neighbour = listRef.current?.querySelector<HTMLButtonElement>(
      `[data-staged-id="${target}"] button`,
    );
    (neighbour ?? fallbackFocusRef.current)?.focus();
  }, [files]);

  if (files.length === 0) {
    return null;
  }
  return (
    <ul ref={listRef} className="flex flex-wrap gap-2">
      {files.map((staged, index) => (
        <li
          key={staged.id}
          data-staged-id={staged.id}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs"
        >
          <span className="max-w-40 truncate font-medium" title={staged.file.name}>
            {staged.file.name}
          </span>
          <button
            type="button"
            // 24×24 minimum hit area (WCAG 2.2 SC 2.5.8).
            className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`${t('myCases.detail.removeAttachment')}: ${staged.file.name}`}
            onFocus={() => {
              focusedIdRef.current = staged.id;
            }}
            onBlur={(event) => {
              // Keep tracking only when the button itself is leaving the DOM (an
              // unmount). A still-connected button lost focus for real — to
              // another element, or to <body> after a click on inert content.
              if (event.relatedTarget !== null || event.currentTarget.isConnected) {
                focusedIdRef.current = null;
              }
            }}
            onClick={() => {
              const neighbour = files[index + 1] ?? files[index - 1];
              pendingFocusRef.current = neighbour !== undefined ? neighbour.id : 'fallback';
              onRemove(staged.id);
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
